import { useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { X } from "lucide-react";
import { SITE, WA_LINK } from "@/lib/site";

const LINKS = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Why Me", href: "#why-me" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.4 });
  return (
    <>
      <motion.div
        data-testid="scroll-progress-bar"
        className="md:hidden fixed top-0 left-0 right-0 h-[3px] bg-ink origin-left z-50"
        style={{ scaleX: progress }}
        aria-hidden="true"
      />
      <header className="fixed top-0 inset-x-0 z-40 bg-cream/95 border-b border-cream-dark" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="font-serif text-xl md:text-2xl uppercase tracking-[0.12em] text-ink">
            {SITE.name}
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="eyebrow text-ink-soft hover:text-ink transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-collaborate"
              className="hidden md:inline-flex items-center bg-ink hover:bg-neutral-800 text-cream text-xs uppercase tracking-[0.14em] font-semibold px-6 py-3 rounded-full transition-colors duration-300"
            >
              Work With Me
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden eyebrow text-ink py-2"
              aria-label="Open menu"
            >
              Menu
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-cream flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-cream-dark">
              <span className="font-serif text-xl uppercase tracking-[0.12em] text-ink">{SITE.name}</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 text-ink">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="font-serif text-5xl text-ink py-3 border-b border-cream-dark"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.4 }}
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-cta"
                onClick={() => setOpen(false)}
                className="mt-8 inline-flex w-fit items-center bg-ink text-cream text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
              >
                Work With Me
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
